import React from "react";
import { useTranslation } from "react-i18next";
import {
  AdminAcquisitionDay,
  AdminAcquisitionPage,
  AdminAcquisitionPlacement,
  acquisitionEngagementPercent,
} from "../../features/admin/models/AdminAcquisition";

const headerClass = "bg-grey-95 dark:bg-primary-20 text-grey-30 dark:text-grey-90";
/** Body text colour, set once on the tbody so every cell is readable in dark mode. */
const bodyClass = "text-grey-20 dark:text-white";
const rowClass = "border-t border-grey-90 dark:border-primary-20";
const numberCellClass = "px-4 py-2 text-right tabular-nums";
const metaCellClass = "px-4 py-2 text-right tabular-nums text-grey-50 dark:text-grey-60";

const formatRate = (percent: number | null): string => (percent === null ? "—" : `${percent}%`);

const formatDay = (isoDay: string): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeZone: "UTC" }).format(
    new Date(`${isoDay}T00:00:00Z`)
  );

/** A count column header followed by its muted "from Meta ads" twin. */
const CountHeaders: React.FC<{ label: string }> = ({ label }) => {
  const { t } = useTranslation();
  return (
    <>
      <th className="px-4 py-2 text-right">{label}</th>
      <th className="px-4 py-2 text-right font-normal">{t("admin.acquisition.metaShort")}</th>
    </>
  );
};

const CountCells: React.FC<{ total: number; meta: number }> = ({ total, meta }) => (
  <>
    <td className={numberCellClass}>{total}</td>
    <td className={metaCellClass}>{meta}</td>
  </>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="with-border rounded-2xl p-8 text-center text-grey-40 dark:text-grey-70">{message}</div>
);

interface AdminAcquisitionDaysTableProps {
  days: AdminAcquisitionDay[];
}

/** Per-UTC-day funnel rows, newest first: views → engaged → taps → arrivals → trial links → accounts. */
export const AdminAcquisitionDaysTable: React.FC<AdminAcquisitionDaysTableProps> = ({ days }) => {
  const { t } = useTranslation();
  return (
    <div className="with-border overflow-x-auto rounded-2xl">
      <table className="w-full text-sm">
        <thead className={headerClass}>
          <tr>
            <th className="px-4 py-2 text-left">{t("admin.acquisition.day")}</th>
            <CountHeaders label={t("admin.acquisition.pageViews")} />
            <CountHeaders label={t("admin.acquisition.engaged")} />
            <CountHeaders label={t("admin.acquisition.ctaTaps")} />
            <CountHeaders label={t("admin.acquisition.arrivals")} />
            <CountHeaders label={t("admin.acquisition.trialLinksRequested")} />
            <CountHeaders label={t("admin.acquisition.trialLinksRedeemed")} />
            <th className="px-4 py-2 text-right">{t("admin.acquisition.signUps")}</th>
          </tr>
        </thead>
        <tbody className={bodyClass}>
          {days.map((day) => (
            <tr key={day.day} className={rowClass}>
              <td className="whitespace-nowrap px-4 py-2">{formatDay(day.day)}</td>
              <CountCells total={day.pageViews} meta={day.metaPageViews} />
              <CountCells total={day.engaged} meta={day.metaEngaged} />
              <CountCells total={day.ctaTaps} meta={day.metaCtaTaps} />
              <CountCells total={day.arrivals} meta={day.metaArrivals} />
              <CountCells total={day.trialLinksRequested} meta={day.metaTrialLinksRequested} />
              <CountCells total={day.trialLinksRedeemed} meta={day.metaTrialLinksRedeemed} />
              <td className={numberCellClass}>{day.signUps}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface AdminAcquisitionPagesTableProps {
  pages: AdminAcquisitionPage[];
}

/** Which marketing-site pages get viewed, read to the end and tapped, most views first. */
export const AdminAcquisitionPagesTable: React.FC<AdminAcquisitionPagesTableProps> = ({ pages }) => {
  const { t } = useTranslation();
  if (pages.length === 0) {
    return <EmptyState message={t("admin.acquisition.noPageViews")} />;
  }
  return (
    <div className="with-border overflow-x-auto rounded-2xl">
      <table className="w-full text-sm">
        <thead className={headerClass}>
          <tr>
            <th className="px-4 py-2 text-left">{t("admin.acquisition.page")}</th>
            <CountHeaders label={t("admin.acquisition.pageViews")} />
            <CountHeaders label={t("admin.acquisition.engaged")} />
            <th className="px-4 py-2 text-right font-normal">
              {t("admin.acquisition.engagedRateShort")}
            </th>
            <CountHeaders label={t("admin.acquisition.ctaTaps")} />
          </tr>
        </thead>
        <tbody className={bodyClass}>
          {pages.map((page) => (
            <tr key={page.page} className={rowClass}>
              <td className="px-4 py-2 font-mono text-xs">{page.page}</td>
              <CountCells total={page.pageViews} meta={page.metaPageViews} />
              <CountCells total={page.engaged} meta={page.metaEngaged} />
              <td className={metaCellClass}>
                {formatRate(acquisitionEngagementPercent(page.pageViews, page.engaged))}
              </td>
              <CountCells total={page.ctaTaps} meta={page.metaCtaTaps} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface AdminAcquisitionPlacementsTableProps {
  placements: AdminAcquisitionPlacement[];
}

/** Which CTA placements get tapped and how many of those taps reach the sign-up screen, most taps first. */
export const AdminAcquisitionPlacementsTable: React.FC<AdminAcquisitionPlacementsTableProps> = ({
  placements,
}) => {
  const { t } = useTranslation();
  if (placements.length === 0) {
    return <EmptyState message={t("admin.acquisition.noArrivals")} />;
  }
  return (
    <div className="with-border overflow-x-auto rounded-2xl">
      <table className="w-full text-sm">
        <thead className={headerClass}>
          <tr>
            <th className="px-4 py-2 text-left">{t("admin.acquisition.placement")}</th>
            <CountHeaders label={t("admin.acquisition.ctaTaps")} />
            <CountHeaders label={t("admin.acquisition.arrivals")} />
            <CountHeaders label={t("admin.acquisition.trialLinksRequested")} />
          </tr>
        </thead>
        <tbody className={bodyClass}>
          {placements.map((placement) => (
            <tr key={placement.placement} className={rowClass}>
              <td className="px-4 py-2">{placement.placement}</td>
              <CountCells total={placement.ctaTaps} meta={placement.metaCtaTaps} />
              <CountCells total={placement.arrivals} meta={placement.metaArrivals} />
              <CountCells total={placement.trialLinksRequested} meta={placement.metaTrialLinksRequested} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
